import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Header.style.css';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import { Dropdown } from 'antd';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddBoxIcon from '@mui/icons-material/AddBox';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InventoryIcon from '@mui/icons-material/Inventory';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import DashboardNotifications from '../../components/dashboard/DashboardNotifications';
import MessagesDropdown from '../../components/dashboard/MessagesDropdown';
import VerifiedBadge from '../common/VerifiedBadge';
import { useDispatch } from 'react-redux';
import logo from '../assets/Carix_logo.png';
import api from '../api.js';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

const NAV_PAGES = [
  { key: 'home',   route: '/',             icon: <HomeIcon fontSize="small" /> },
  { key: 'buyCar', route: '/featured-cars', icon: <DirectionsCarIcon fontSize="small" /> },
];

export const Header = () => {
  const dispatch     = useDispatch();
  const storageColor = localStorage.getItem('color');
  const navigate     = useNavigate();
  const { t }        = useTranslation();

  const [color, setColor]             = useState(() => storageColor ?? '');
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);

  // Dropdowns séparés desktop/mobile : les deux Box (desktop/mobile) restent
  // montées en même temps (seul le CSS display change), donc partager un seul
  // état d'ouverture ouvrait les deux popups à la fois (effet "fenêtre en double").
  const [openNotifDesktop, setOpenNotifDesktop] = useState(false);
  const [openNotifMobile, setOpenNotifMobile]   = useState(false);
  const [openMsgsDesktop, setOpenMsgsDesktop]   = useState(false);
  const [openMsgsMobile, setOpenMsgsMobile]     = useState(false);

  const isLoggedIn   = localStorage.getItem('isLoggedIn') === 'true';
  const role         = localStorage.getItem('role');
  const fullname     = localStorage.getItem('fullname');
  const [profileImage, setProfileImage] = useState(() => localStorage.getItem('profileImage'));
  const [emailConfirmed, setEmailConfirmed] = useState(() => localStorage.getItem('emailConfirmed'));
  const [isVerified, setIsVerified] = useState(() => localStorage.getItem('isVerified') === 'true');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const isPro        = isLoggedIn && role?.toLowerCase() === 'professionnel';
  const isAdmin       = isLoggedIn && role === 'Admin';
  const unreadCount  = notifications.filter(n => !n.isRead).length;
  const unreadMsgCount = inboxMessages.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  const avatarSrc = profileImage
    ? `${process.env.REACT_APP_API_BASE_URL}/profile-images/${profileImage}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(fullname || "U")}&background=5F41E4&color=fff&size=200&bold=true`;

  useEffect(() => {
    if (!isLoggedIn) return;
    api.get('/notifications/my').then(r => setNotifications(r.data)).catch(() => {});
    api.get('/messages/inbox').then(r => setInboxMessages(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem('color', color);
  }, [color]);

  useEffect(() => {
    const syncProfile = () => {
      setProfileImage(localStorage.getItem('profileImage'));
      setEmailConfirmed(localStorage.getItem('emailConfirmed'));
      setIsVerified(localStorage.getItem('isVerified') === 'true');
    };
    window.addEventListener('profile-updated', syncProfile);
    window.addEventListener('storage', syncProfile);
    return () => {
      window.removeEventListener('profile-updated', syncProfile);
      window.removeEventListener('storage', syncProfile);
    };
  }, []);

  const handleResendConfirmation = async () => {
    try {
      setResendingEmail(true);
      await api.post('/account/resend-confirmation-email');
      setResendSent(true);
    } catch {
      // silencieux : l'utilisateur peut réessayer
    } finally {
      setResendingEmail(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/account/logout');
    } catch {
      // le cookie expirera de toute facon, on continue le nettoyage local
    }
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('fullname');
    localStorage.removeItem('profileImage');
    localStorage.removeItem('emailConfirmed');
    localStorage.removeItem('isVerified');
    setAnchorElUser(null);
    setDrawerOpen(false);
    window.location.reload();
  };

  const go = (route) => { navigate(route); setDrawerOpen(false); };

  /* ── Mobile Drawer ───────────────────────────────────── */
  const MobileDrawer = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{ sx: { width: 280, background: '#0d0f1a', color: '#fff', display: 'flex', flexDirection: 'column' } }}
    >
      {/* Drawer header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <img src={logo} style={{ height: 40, objectFit: 'contain' }} alt="logo" />
        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User info if logged in */}
      {isLoggedIn && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '16px 20px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Avatar
            src={avatarSrc}
            sx={{ width: 44, height: 44 }}
          />
          <Box>
            <Box sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff', display: 'flex', alignItems: 'center' }}>{fullname || t('userFallback')}{isVerified && <VerifiedBadge size={15} />}</Box>
            <Box sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>{t('myAccount')}</Box>
          </Box>
        </Box>
      )}

      {/* Nav links */}
      <List sx={{ pt: 1 }}>
        {NAV_PAGES.map(p => (
          <ListItem key={p.key} disablePadding>
            <ListItemButton onClick={() => go(p.route)} sx={{ px: 2.5, py: 1.2, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
              <ListItemIcon sx={{ color: '#a78bfa', minWidth: 36 }}>{p.icon}</ListItemIcon>
              <ListItemText primary={t(p.key)} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        ))}

        <ListItem disablePadding>
          <ListItemButton onClick={() => { if (!isLoggedIn) localStorage.setItem('redirectAfterLogin', '/AddCars'); go(isLoggedIn ? '/AddCars' : '/login'); }} sx={{ px: 2.5, py: 1.2, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
            <ListItemIcon sx={{ color: '#a78bfa', minWidth: 36 }}><AddBoxIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('sellCar')} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
          </ListItemButton>
        </ListItem>
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1 }} />

      {/* Auth or user menu items */}
      <List sx={{ pb: 1 }}>
        {isLoggedIn ? (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => go('/my-cars')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><InventoryIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('headerMyListings')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => go('/favorites')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><FavoriteIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('headerFavorites')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => go('/Messages')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><ChatBubbleIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('chat')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => go('/profilePage')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><AccountCircleIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('headerProfile')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
            {isPro && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => go('/dashboard')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                  <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><DashboardIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => go('/admin/overview')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                  <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><DashboardIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={t('adminPanel')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>
            )}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(239,68,68,0.1)' } }}>
                <ListItemIcon sx={{ color: '#ef4444', minWidth: 36 }}><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('loginOut')} primaryTypographyProps={{ fontSize: '0.9rem', color: '#ef4444' }} />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => go('/login')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><LoginIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('login')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => go('/register')} sx={{ px: 2.5, py: 1.1, '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                <ListItemIcon sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 36 }}><PersonAddIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={t('register')} primaryTypographyProps={{ fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>

      {/* Language switcher + dark mode at bottom */}
      <Box sx={{ mt: 'auto', p: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Box sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', mb: 1, letterSpacing: '1px', textTransform: 'uppercase' }}>Langue</Box>
          <LanguageSwitcher />
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar className="navbar" position="sticky" elevation={0} sx={{ boxShadow: 'none' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '64px !important' }}>

            {/* ── Logo ─────────────────────────────────── */}
            <Box component="a" href="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
              <img src={logo} style={{ height: 48, objectFit: 'contain' }} alt="Carix" />
            </Box>

            {/* ── Desktop nav ───────────────────────────── */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 0.5, ml: 3 }}>
              {NAV_PAGES.map(p => (
                <Button key={p.key} className="nav-link-btn" onClick={() => navigate(p.route)} sx={{ color: 'white' }}>
                  {t(p.key)}
                </Button>
              ))}
            </Box>

            {/* ── Desktop right ─────────────────────────── */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
              <LanguageSwitcher />

              {!isLoggedIn ? (
                <>
                  <Button variant="outlined" className="auth-btn-outline" sx={{ color: 'white' }} onClick={() => navigate('/login')}>
                    {t('login')}
                  </Button>
                  <Button variant="outlined" className="auth-btn-outline" sx={{ color: 'white' }} onClick={() => navigate('/register')}>
                    {t('register')}
                  </Button>
                  <Button variant="contained" className="sell-btn" disableElevation
                    onClick={() => { localStorage.setItem('redirectAfterLogin', '/AddCars'); navigate('/login'); }}>
                    {t('sellCar')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="contained" className="sell-btn" disableElevation onClick={() => navigate('/AddCars')}>
                    {t('sellCar')}
                  </Button>

                  <Dropdown open={openMsgsDesktop} onOpenChange={setOpenMsgsDesktop}
                    popupRender={() => <MessagesDropdown open={openMsgsDesktop} onClose={() => setOpenMsgsDesktop(false)} />}
                    placement="bottomRight" trigger={['click']}>
                    <IconButton className="notif-btn">
                      <Badge badgeContent={unreadMsgCount || null} color="error">
                        <ChatBubbleIcon sx={{ color: 'rgba(255,255,255,0.85)' }} />
                      </Badge>
                    </IconButton>
                  </Dropdown>

                  <Dropdown open={openNotifDesktop} onOpenChange={setOpenNotifDesktop}
                    popupRender={() => <DashboardNotifications open={openNotifDesktop} onClose={() => setOpenNotifDesktop(false)} />}
                    placement="bottomRight" trigger={['click']}>
                    <IconButton className="notif-btn">
                      <Badge badgeContent={unreadCount || null} color="error">
                        <NotificationsIcon sx={{ color: 'rgba(255,255,255,0.85)' }} />
                      </Badge>
                    </IconButton>
                  </Dropdown>

                  <Tooltip title={t('myAccount')}>
                    <Button className="user-pill" onClick={e => setAnchorElUser(e.currentTarget)}>
                      <Avatar
                        src={avatarSrc}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>{fullname || 'Utilisateur'}{isVerified && <VerifiedBadge size={15} />}</Box>
                    </Button>
                  </Tooltip>

                  <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={() => setAnchorElUser(null)}
                    PaperProps={{ className: 'user-menu-paper' }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    sx={{ mt: 1 }}>
                    <MenuItem className="user-menu-item" onClick={() => { setAnchorElUser(null); navigate('/my-cars'); }}>
                      <InventoryIcon fontSize="small" /> {t('headerMyListings')}
                    </MenuItem>
                    <MenuItem className="user-menu-item" onClick={() => { setAnchorElUser(null); navigate('/favorites'); }}>
                      <FavoriteIcon fontSize="small" /> {t('headerFavorites')}
                    </MenuItem>
                    <MenuItem className="user-menu-item" onClick={() => { setAnchorElUser(null); navigate('/Messages'); }}>
                      <ChatBubbleIcon fontSize="small" /> {t('chat')}
                    </MenuItem>
                    <MenuItem className="user-menu-item" onClick={() => { setAnchorElUser(null); navigate('/profilePage'); }}>
                      <AccountCircleIcon fontSize="small" /> {t('headerProfile')}
                    </MenuItem>
                    {isPro && (
                      <MenuItem className="user-menu-item" onClick={() => { setAnchorElUser(null); navigate('/dashboard'); }}>
                        <DashboardIcon fontSize="small" /> Dashboard
                      </MenuItem>
                    )}
                    {isAdmin && (
                      <MenuItem className="user-menu-item" onClick={() => { setAnchorElUser(null); navigate('/admin/overview'); }}>
                        <DashboardIcon fontSize="small" /> {t('adminPanel')}
                      </MenuItem>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem className="user-menu-item user-menu-logout" onClick={handleLogout}>
                      <LogoutIcon fontSize="small" /> {t('loginOut')}
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>

            {/* ── Mobile right ──────────────────────────── */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
              {/* Messages si connecté */}
              {isLoggedIn && (
                <Dropdown open={openMsgsMobile} onOpenChange={setOpenMsgsMobile}
                  popupRender={() => <MessagesDropdown open={openMsgsMobile} onClose={() => setOpenMsgsMobile(false)} />}
                  placement="bottomRight" trigger={['click']}>
                  <IconButton className="notif-btn" size="small">
                    <Badge badgeContent={unreadMsgCount || null} color="error">
                      <ChatBubbleIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 20 }} />
                    </Badge>
                  </IconButton>
                </Dropdown>
              )}

              {/* Notifications si connecté */}
              {isLoggedIn && (
                <Dropdown open={openNotifMobile} onOpenChange={setOpenNotifMobile}
                  popupRender={() => <DashboardNotifications open={openNotifMobile} onClose={() => setOpenNotifMobile(false)} />}
                  placement="bottomRight" trigger={['click']}>
                  <IconButton className="notif-btn" size="small">
                    <Badge badgeContent={unreadCount || null} color="error">
                      <NotificationsIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 20 }} />
                    </Badge>
                  </IconButton>
                </Dropdown>
              )}

              {/* Avatar si connecté */}
              {isLoggedIn && (
                <Avatar
                  src={avatarSrc}
                  sx={{ width: 32, height: 32, cursor: 'pointer' }}
                  onClick={() => setDrawerOpen(true)}
                />
              )}

              {/* Hamburger */}
              <IconButton
                size="large"
                onClick={() => setDrawerOpen(true)}
                sx={{ color: 'rgba(255,255,255,0.85)', p: 1 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

          </Toolbar>
        </Container>
      </AppBar>

      {/* Bandeau email non confirmé */}
      {isLoggedIn && emailConfirmed === 'false' && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexWrap: 'wrap', gap: 1.5, textAlign: 'center',
            background: '#fff8e1', color: '#8a6d00',
            padding: '10px 16px', fontSize: '0.85rem', fontWeight: 500,
          }}
        >
          <span>
            ⚠️ {t('emailNotConfirmedWarning')}
          </span>
          <Button
            size="small"
            onClick={handleResendConfirmation}
            disabled={resendingEmail || resendSent}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
              color: '#8a6d00', textDecoration: 'underline', minWidth: 'auto', p: 0,
            }}
          >
            {resendSent ? `${t('emailResent')} ✓` : resendingEmail ? t('sendingInProgress') : t('resendConfirmationEmail')}
          </Button>
        </Box>
      )}

      {/* Mobile Drawer */}
      {MobileDrawer}
    </>
  );
};
